import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import BrushOutlinedIcon from "@mui/icons-material/BrushOutlined";
import MoreHorizOutlinedIcon from "@mui/icons-material/MoreHorizOutlined";

import styles from "./index.module.css";
import CustomDropdown from "../CustomDropdownWithPlus";
import CustomTextField from "../CustomTextField";
import { getTaxes } from "../../services/tax";
import { addOrderOtherService, updateOtherService } from "../../services/orderItem";
import { ToastContainer, toast } from "react-toastify";
import ButtonLoader from "../ButtonLoader";
import axios from "axios";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { alignEditedShippingServiceTax, decimalInputToNumber, findOtherServiceTaxById, isDecimalInput, isWholeNumberInput } from "../../utils/otherServicePricing";

const TYPE_OPTIONS = [
  { key: "Shipping", label: "Shipping", Icon: LocalShippingOutlinedIcon },
  { key: "Art", label: "Art", Icon: BrushOutlinedIcon },
  { key: "Other", label: "Other", Icon: MoreHorizOutlinedIcon },
];

// helpers
const toNum = (v) => (v === "" || v === null || v === undefined ? 0 : decimalInputToNumber(v));
const fixed2 = (n) => +Number(n || 0).toFixed(2);
// put near your helpers
const normalizeTaxPercent = (v) => {
  if (v === null || v === undefined || v === "") return null;
  let n = Number(v);
  if (!Number.isFinite(n)) return null;
  // Accept 0.05 (decimal) or 5 (percent)
  if (n > 0 && n <= 1) n = n * 100; // 0.05 → 5
  return +n.toFixed(6); // keep it clean
};

/**
 * Choose a 2-decimal retail so ((retail - cost)/retail)*100 rounds to target %.
 */
function retailForMargin(cost, targetPct) {
  const uc = toNum(cost);
  const m = toNum(targetPct);
  if (uc <= 0) return 0;
  if (m <= 0) return fixed2(uc);
  const targetBP = Math.round(m * 100); // basis points
  const ideal = uc / (1 - targetBP / 10000);
  const idealC = Math.round(ideal * 100); // cents
  const candidates = [idealC - 2, idealC - 1, idealC, idealC + 1, idealC + 2].filter((c) => c > 0);

  for (const r of candidates) {
    const retail = r / 100;
    const eff = ((retail - uc) / retail) * 100;
    if (Math.round(eff * 100) === targetBP) return fixed2(retail);
  }
  // fallback: closest to ideal
  let best = candidates[0],
    bestDiff = Math.abs(candidates[0] - idealC);
  for (const r of candidates) {
    const d = Math.abs(r - idealC);
    if (d < bestDiff) {
      best = r;
      bestDiff = d;
    }
  }
  return fixed2(best / 100);
}

const DEFAULT_FORM = {
  Type: "Other",
  title: "",
  description: "",
  quantity: 0,
  unitCost: 0,
  margin: 0, // 0 is valid
  retailPrice: 0,
  total: 0,
  // store percent as Number or null (null = no tax)
  selectedTax: null,
  selectedTaxRecordId: null,
};

const OtherServiceModal = ({
  open,
  onClose,
  fetchOrder,
  orderId,
  fetchedOtherServiceData,
  otherServiceId,
  controlledMode = false,
  initialService = null,
  onSaveService = null,
  source = "order-cart",
}) => {
  const { instanceUniqueId } = useParams();
  const [errors, setErrors] = useState({});
  const [taxOptionsRaw, setTaxOptionsRaw] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [retailOverridden, setRetailOverridden] = useState(false);
  const [defaultMargin, setDefaultMargin] = useState(40);
  const [defaultTaxId, setDefaultTaxId] = useState(null);
  const isSalesOrderShippingTax = controlledMode &&
    source === "sales-order-cart" && formData.Type === "Shipping";

  // Keep legacy Order Cart/Other Service percentage selection intact.
  // Sales Order Shipping uses the actual catalogue tax ID, since different
  // configured tax records can have the same percentage.
  const taxOptions = useMemo(() => {
    const mapped = (taxOptionsRaw || []).map((t) => {
      let pct = t && typeof t === "object"
        ? (t.Percent ?? t.percent ?? t.rate ?? t.value ?? t.percentage ?? t.id ?? t.ID ?? 0)
        : t;
  
      pct = normalizeTaxPercent(pct); // ← normalize here
  
      const name =
        (t && typeof t === "object"
          ? (t.Name ?? t.TaxName ?? t.label ?? t.name ?? `Tax (${pct ?? 0}%)`)
          : `Tax (${pct ?? 0}%)`);
  
      const recordId = t && typeof t === "object"
        ? Number(t.id ?? t.taxId ?? t.ID)
        : null;
      return {
        id: isSalesOrderShippingTax
          ? (Number.isInteger(recordId) && recordId > 0 ? recordId : null)
          : pct,
        name: `${name} (${pct ?? 0}%)`,
      };
    }).filter((option) => !isSalesOrderShippingTax || option.id != null);
    return [{ id: null, name: "— No tax —" }, ...mapped];
  }, [taxOptionsRaw, isSalesOrderShippingTax]);

  const defaultTaxPercent = useMemo(() => {
    if (!defaultTaxId) return null;
    const hit = (taxOptionsRaw || []).find((t) => Number(t?.id ?? t?.ID) === Number(defaultTaxId));
    const pct = normalizeTaxPercent(hit?.value ?? hit?.Value ?? hit?.Percent ?? hit?.percent ?? hit?.rate);
    return pct == null ? null : pct;
  }, [taxOptionsRaw, defaultTaxId]);

  const isEditing = Boolean(
    otherServiceId ||
    fetchedOtherServiceData?.id ||
    initialService?.id ||
    initialService?.cartServiceId
  );
  
  // Robust (re)initialize whenever we open OR item changes
  useEffect(() => {
    if (!open) return;

    const src = controlledMode
      ? initialService
      : (fetchedOtherServiceData || initialService);
    if (src) {
      const data = {
        Type: src.Type || "Other",
        title: src.Title || "",
        description: src.Description || "",
        quantity: toNum(src.Quantity),
        unitCost: fixed2(src.UnitCost),
        margin: toNum(src.Margin),
        retailPrice: fixed2(src.RetailPrice),
        total: fixed2(src.Total),
        selectedTax:
          src.Taxes === undefined || src.Taxes === null || src.Taxes === ""
            ? null
            // OtherServices.Taxes is already a percentage, so 1 means 1%,
            // not a 100% catalogue decimal rate.
            : controlledMode && source === "sales-order-cart" && src.Type === "Shipping"
              ? Number(src.Taxes)
              : normalizeTaxPercent(src.Taxes),
        selectedTaxRecordId: src?.TaxId ?? src?.taxId ??
          src?.taxIds?.[0] ?? src?.TaxIds?.[0] ?? src?.taxDetails?.taxId ?? null,

      };

      // if shipping, enforce qty 1
      if (data.Type === "Shipping") data.quantity = 1;

      setRetailOverridden(false);
      setFormData((prev) => ({ ...prev, ...data }));
      setErrors({});
    } else {
      // creating new
      const fresh = { ...DEFAULT_FORM, margin: defaultMargin, selectedTax: defaultTaxPercent, selectedTaxRecordId: defaultTaxId };
      recalc(fresh, { fromField: "init" });
      setRetailOverridden(false);
      setFormData(fresh);
      setErrors({});
    }
  }, [open, otherServiceId, fetchedOtherServiceData, initialService, controlledMode, defaultMargin, defaultTaxPercent]);

  useEffect(() => {
    const fetchDefaults = async () => {
      if (!instanceUniqueId) return;
      try {
        const instanceRes = await axios.get(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/byinstanceUniqueId/${instanceUniqueId}`
        );
        const instanceId = instanceRes?.data?.id;
        if (!instanceId) return;
        const defaultsRes = await axios.get(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/${instanceId}/defaults`,
          { headers: { accessToken: localStorage.getItem("accessToken") } }
        );
        const defaults = defaultsRes?.data?.defaults || {};
        const nextDefaultMargin = Number(defaults?.defaultMargin);
        setDefaultMargin(Number.isFinite(nextDefaultMargin) ? nextDefaultMargin : 40);
        setDefaultTaxId(defaults?.defaultTaxId ? Number(defaults.defaultTaxId) : null);
      } catch {
        setDefaultMargin(40);
        setDefaultTaxId(null);
      }
    };
    fetchDefaults();
  }, [instanceUniqueId]);

  // fetch taxes once
  useEffect(() => {
    (async () => {
      try {
        const taxesData = await getTaxes();
        setTaxOptionsRaw(taxesData?.taxes || []);
      } catch {
        setTaxOptionsRaw([]);
      }
    })();
  }, []);

  const recalc = (draft, { fromField } = {}) => {
    const q = toNum(draft.quantity);
    const uc = toNum(draft.unitCost);
    const m = toNum(draft.margin);
    const taxPct = draft.selectedTax == null ? 0 : toNum(draft.selectedTax); // already percent
  
    let retail;
    if (fromField === "retailPrice" || (retailOverridden && fromField !== "unitCost" && fromField !== "margin")) {
      retail = toNum(draft.retailPrice);
    } else {
      retail = retailForMargin(uc, m);
    }
  
    const subtotal = retail * q;
    const taxAmount = subtotal * (taxPct / 100); // ← percent to decimal
    const total = subtotal + taxAmount;
  
    // Keep intermediate states such as "28." intact while editing retail.
    if (fromField !== "retailPrice") draft.retailPrice = fixed2(retail);
    draft.total = fixed2(total);
  };  

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Quantity remains whole-unit; monetary and percentage fields retain decimal
    // strings until save so a trailing decimal separator is a valid typing state.
    if (name === "quantity" && !isWholeNumberInput(value)) return;
    if (["unitCost", "margin", "retailPrice"].includes(name) && !isDecimalInput(value)) return;

    const next = { ...formData, [name]: value };

    // If Type is Shipping, always force quantity 1
    if (name === "Type" && value === "Shipping") {
      next.quantity = 1;
    }

    // Toggle override flag
    if (name === "retailPrice") setRetailOverridden(true);
    if (name === "unitCost" || name === "margin") setRetailOverridden(false);

    // Recalc when relevant fields change
    if (["unitCost", "margin", "quantity", "retailPrice"].includes(name)) {
      recalc(next, { fromField: name });
    }

    setFormData(next);

    if (errors[name] && String(value).trim().length) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSelect = (event, name) => {
    let value = event?.target?.value;
    if (name === "selectedTax" && isSalesOrderShippingTax) {
      const selectedRecord = findOtherServiceTaxById(taxOptionsRaw, value);
      const next = {
        ...formData,
        selectedTaxRecordId: selectedRecord?.id ?? selectedRecord?.taxId ?? selectedRecord?.ID ?? null,
        selectedTax: selectedRecord
          ? normalizeTaxPercent(selectedRecord.value ?? selectedRecord.rate ?? selectedRecord.Percent ?? selectedRecord.percent)
          : null,
      };
      recalc(next, { fromField: name });
      setFormData(next);
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
      return;
    }
    if (name === "selectedTax") {
      if (value === "" || value === undefined || value === "null") value = null;
      else value = normalizeTaxPercent(value); // ← normalize here
    }
    const next = { ...formData, [name]: value };
    if (name === "selectedTax") recalc(next, { fromField: name });
    setFormData(next);
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };
  
  const handleTypeClick = (typeKey) => {
    const next = { ...formData, Type: typeKey };
    if (typeKey === "Shipping") {
      next.quantity = 1;
      if (controlledMode && source === "sales-order-cart") {
        const defaultRecord = findOtherServiceTaxById(taxOptionsRaw, defaultTaxId);
        const defaultPct = normalizeTaxPercent(defaultRecord?.rate ?? defaultRecord?.value);
        next.selectedTaxRecordId = defaultRecord && defaultPct === Number(next.selectedTax)
          ? (defaultRecord.id ?? defaultRecord.taxId ?? defaultRecord.ID)
          : null;
      }
    }
    recalc(next, { fromField: "Type" });
    setFormData(next);
    setErrors((prev) => ({ ...prev, Type: null }));
  };

  const validate = () => {
    const requiredFields = ["title", "quantity", "unitCost", "margin", "Type"];
    const newErrors = {};
    requiredFields.forEach((field) => {
      const v = formData?.[field];
      const isMissing =
        v === undefined ||
        v === null ||
        (typeof v === "string" && v.trim() === "");
      if (isMissing) newErrors[field] = "This field is required";
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    // Final calc to be safe
    const draft = { ...formData };
    recalc(draft);
    const rateMatches = (taxOptionsRaw || []).filter((t) => {
      const pct = normalizeTaxPercent(t?.value ?? t?.Value ?? t?.Percent ?? t?.percent ?? t?.rate);
      return draft.selectedTax != null && pct != null && Number(pct) === Number(draft.selectedTax);
    });
    const resolvedTax = isSalesOrderShippingTax
      ? findOtherServiceTaxById(taxOptionsRaw, draft.selectedTaxRecordId) ||
        (draft.selectedTaxRecordId == null && rateMatches.length === 1 ? rateMatches[0] : null)
      : rateMatches[0];
    if (isSalesOrderShippingTax && draft.selectedTax != null && !resolvedTax) {
      setErrors((prev) => ({ ...prev, selectedTax: "Select a specific tax from the catalogue." }));
      return;
    }
    if (isSalesOrderShippingTax && resolvedTax && draft.selectedTax != null) {
      const catalogueRate = normalizeTaxPercent(resolvedTax?.value ?? resolvedTax?.rate ??
        resolvedTax?.Percent ?? resolvedTax?.percent);
      if (catalogueRate == null || Math.abs(Number(catalogueRate) - Number(draft.selectedTax)) > 0.000001) {
        setErrors((prev) => ({ ...prev, selectedTax: "The saved tax rate no longer matches this catalogue entry. Select the tax again." }));
        return;
      }
    }

    const payload = {
      Type: draft.Type,
      Title: draft.title,
      Description: draft.description,
      Quantity: toNum(draft.quantity),
      UnitCost: toNum(draft.unitCost),
      Margin: toNum(draft.margin),
      RetailPrice: toNum(draft.retailPrice),
      Total: toNum(draft.total),
      // null = no tax; number = percent
      Taxes: draft.selectedTax === null ? null : toNum(draft.selectedTax),
      TaxId: resolvedTax?.id ?? resolvedTax?.ID ?? null,
    };

    try {
      setLoading(true);

      if (controlledMode) {
        const isSalesOrderShippingEdit = source === "sales-order-cart" && payload.Type === "Shipping";
        const editedService = isSalesOrderShippingEdit
          ? alignEditedShippingServiceTax(initialService || {}, payload, resolvedTax)
          : { ...(initialService || {}), ...payload };
        const servicePayload = {
          ...editedService,
          id: initialService?.id ?? otherServiceId ?? null,
          cartServiceId: initialService?.cartServiceId ?? otherServiceId ?? null,
          originalOtherServiceId: initialService?.originalOtherServiceId ?? initialService?.id ?? null,
          Source: source,
        };
        // Historical edit markers must not freeze subsequent shipping changes.
        if (isSalesOrderShippingEdit) delete servicePayload.salesOrderCartShippingEdited;
        if (typeof onSaveService === "function") await onSaveService(servicePayload);
        onClose();
        return;
      }

      if (isEditing) {
        await updateOtherService(
          otherServiceId || fetchedOtherServiceData?.id || initialService?.id || initialService?.cartServiceId,
          payload
        );
        toast.success("Service updated successfully", { toastId: "other-service" });
      } else {
        await addOrderOtherService(orderId, payload);
        toast.success("Service added successfully", { toastId: "other-service" });
      }

      await fetchOrder();
      onClose();
    } catch (error) {
      console.error(error);
      toast.error("Failed to save service", { toastId: "other-service" });
    } finally {
      setLoading(false);
    }
  };

  const TypeButton = ({ typeKey, label, Icon }) => {
    const selected = formData.Type === typeKey;
    return (
      <Button
        onClick={() => handleTypeClick(typeKey)}
        aria-pressed={selected}
        startIcon={<Icon />}
        variant={selected ? "contained" : "outlined"}
        color={selected ? "primary" : "inherit"}
        sx={{
          width: "33.3333%",
          height: 64,
          borderRadius: 8,
          textTransform: "none",
          fontWeight: 600,
          ...(selected
            ? {}
            : {
                borderColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.2)"
                    : "rgba(0,0,0,0.2)",
              }),
        }}
      >
        {label}
      </Button>
    );
  };

  const FieldError = ({ name }) =>
    errors[name] ? (
      <Typography
        variant="caption"
        color="error"
        style={{ marginTop: 4, display: "block" }}
      >
        {errors[name]}
      </Typography>
    ) : null;

  const isShipping = formData.Type === "Shipping";

  return (
    <>
      <ToastContainer />
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        keepMounted  // helps avoid stale/unmounted flashes
      >
        <DialogTitle
          className="create-order-dialog-title"
          style={{
            margin: 0,
            padding: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Other Service {formData.title ? ` - ${formData.title}` : ""}
          <IconButton aria-label="close" onClick={onClose} size="large">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent className="create-order-dialog-content">
          {/* Type selector row */}
          <div style={{ display: "flex", gap: 12, marginBottom: 24, marginTop: 8 }}>
            {TYPE_OPTIONS.map(({ key, label, Icon }) => (
              <TypeButton key={key} typeKey={key} label={label} Icon={Icon} />
            ))}
          </div>
          <FieldError name="Type" />

          {/* Full width fields */}
          <div style={{ marginBottom: 16 }}>
            <CustomTextField
              label="Title"
              error={Boolean(errors.title)}
              helperText={errors.title}
              name="title"
              placeholder="Enter title"
              value={formData.title}
              handleChange={handleInputChange}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <CustomTextField
              label="Description"
              name="description"
              placeholder="Enter description"
              value={formData.description}
              handleChange={handleInputChange}
            />
          </div>

          {/* Grid */}
          <div className={styles["other-service-double-grid"]}>
            <div className={styles["other-service-double-grid-column"]}>
              <CustomTextField
                label="Quantity"
                error={Boolean(errors.quantity)}
                helperText={errors.quantity}
                name="quantity"
                placeholder="Enter quantity"
                value={isShipping ? 1 : formData.quantity}
                handleChange={handleInputChange}
                disabled={isShipping} // force 1 for Shipping
              />
            </div>

            <div className={styles["other-service-double-grid-column"]}>
              <CustomTextField
                label="Unit Cost"
                error={Boolean(errors.unitCost)}
                helperText={errors.unitCost}
                name="unitCost"
                placeholder="Enter unit cost"
                value={formData.unitCost}
                handleChange={handleInputChange}
              />
            </div>

            <div className={styles["other-service-double-grid-column"]}>
              <CustomTextField
                label="Margin %"
                error={Boolean(errors.margin)}
                helperText={errors.margin}
                name="margin"
                placeholder="Enter margin % (0 allowed)"
                value={formData.margin}
                handleChange={handleInputChange}
              />
            </div>

            <div className={styles["other-service-double-grid-column"]}>
              <CustomTextField
                label="Retail Price"
                name="retailPrice"
                placeholder="Retail price"
                value={formData.retailPrice}
                handleChange={handleInputChange}
              />
            </div>

            <div className={styles["other-service-double-grid-column"]}>
              <CustomDropdown
                label="Default Tax (%)"
                selectedValue={isSalesOrderShippingTax ? formData.selectedTaxRecordId : formData.selectedTax}
                name="selectedTax"
                options={taxOptions}
                handleSelect={(e) => handleSelect(e, "selectedTax")}
              />
              <FieldError name="selectedTax" />
            </div>

            <div className={styles["other-service-double-grid-column"]}>
              <CustomTextField
                label="Total (incl. tax)"
                name="Total"
                disabled
                placeholder="Total"
                value={formData.total}
                handleChange={handleInputChange}
              />
            </div>
          </div>
        </DialogContent>

        <div className={styles.buttonContainer}>
          <Button onClick={handleSave}>
            {loading ? <ButtonLoader /> : isEditing ? "Update" : "Add service"}
          </Button>
        </div>
      </Dialog>
    </>
  );
};

export default OtherServiceModal;
